import {
  AirRestrictionStatus,
  DullesAmenityCanExist,
  DullesUta,
  FareDetails,
} from "@hopper-b2b/types";
import { camelCaseToTitleCase, capitalize } from "@hopper-b2b/utilities";
import { Box, Popover, Typography } from "@mui/material";
import { MouseEvent, useMemo, useState } from "react";
import Amenity from "./Amenity";
import { IRestrictionProps } from "./Restriction";
import { IconComponent } from "../IconComponent";
import { IconName } from "../Icon";
import { IFareNotice } from "./component";
import { TFunction } from "i18next";
import { useI18nContext } from "@hopper-b2b/i18n";

interface ISelectedFareRestrictionsProps {
  selectedFare: FareDetails;
  allFareDetails: FareDetails[];
  fareNotice?: IFareNotice[];
  index: number;
  isOutgoing: boolean;
  isMobile?: boolean;
  getEmptyRestrictionsText: (
    fareRating: number | undefined,
    translate: TFunction
  ) => string;
}
export const isDescriptionShort = (description: string, isMobile: boolean) => {
  if (isMobile) {
    // note: assuming each character is roughly 6px; it's considered short if it's less than 170px.
    return description.length * 6 < 170;
  } else {
    // note: assuming each character is roughly 6px; it's considered short if it's less than 270px.
    return description.length * 6 < 270;
  }
};

const SelectedFareRestrictions = ({
  selectedFare,
  allFareDetails,
  fareNotice,
  index,
  isOutgoing,
  isMobile,
  getEmptyRestrictionsText,
}: ISelectedFareRestrictionsProps) => {
  const { t } = useI18nContext();
  const [showAllAmenities, setShowAllAmenities] = useState<boolean>(
    allFareDetails.length < 2
  );
  const [hackerFareAnchorEl, setHackerFareAnchorEl] =
    useState<HTMLElement | null>(null);
  const fareIndex = selectedFare.slices.findIndex((slice) =>
    isOutgoing ? slice.outgoing : !slice.outgoing
  );
  const fareSlice = selectedFare.slices[fareIndex];

  const handleSeeMoreOnClick = () => setShowAllAmenities(!showAllAmenities);

  const restrictions: IRestrictionProps[] = [];

  restrictions.push(
    ...(fareSlice.amenitiesUtas?.utas.utas.map((uta: DullesUta) => {
      const restriction: IRestrictionProps = {
        // TODO: the assessment field should probably be an enum
        // Dulles for Uber is giving us incorrect info for several categories (hack)
        symbol:
          uta.category === "boarding-priority" &&
          ["fee", "restriction"].includes(uta.assessment)
            ? AirRestrictionStatus.GENERIC
            : uta.category === "carry-on-allowance" &&
                uta.assessment === "restriction"
              ? AirRestrictionStatus.GENERIC
              : uta.category === "cancellation" &&
                  uta.headline === "Non-refundable" // !!! translated strings will not match this copy
                ? AirRestrictionStatus.GENERIC
                : uta.assessment === "benefit"
                  ? AirRestrictionStatus.INCLUDED
                  : uta.assessment === "fee"
                    ? AirRestrictionStatus.PURCHASABLE
                    : uta.assessment === "restriction"
                      ? AirRestrictionStatus.UNAVAILABLE
                      : AirRestrictionStatus.GENERIC,
        // TODO: probably need a different type for category
        description: `${uta.description}`,
      };

      return restriction;
    }) ?? [])
  );
  if (fareSlice.amenitiesUtas?.amenities) {
    restrictions.push(
      ...Object.keys(fareSlice.amenitiesUtas.amenities).map((key) => {
        const amenitity: DullesAmenityCanExist = (
          fareSlice.amenitiesUtas?.amenities as any
        )?.[key];
        const symbol =
          amenitity.exists === "yes"
            ? AirRestrictionStatus.INCLUDED
            : amenitity.exists === "no"
              ? AirRestrictionStatus.UNAVAILABLE
              : AirRestrictionStatus.GENERIC;
        return {
          symbol: symbol,
          description: `${t(`amenitiesCategories.${key}`)} - ${
            amenitity.info.displayText
          }`,
        };
      })
    );
  }

  const amenitiesCountToShow = useMemo(() => {
    if (showAllAmenities) {
      return restrictions.length;
    }
    // note: if the last item is long, only show N-1 amenities; otherwise, show N amenities; N = restrictionsCount.
    const getCount = (restrictionsCount: number) => {
      if (restrictions.length >= restrictionsCount)
        return isDescriptionShort(
          restrictions[restrictionsCount - 1].description,
          isMobile
        )
          ? restrictionsCount
          : restrictionsCount - 1;
      else return restrictionsCount - 1;
    };

    switch (allFareDetails.length) {
      case 2:
        // note: if the last item is long, only show 6 amenities; otherwise, show 7 amenities.
        return getCount(7);
      case 3:
        // note: if the last item is long, only show 3 amenities; otherwise, show 4 amenities.
        return getCount(4);
      default:
        return 3;
    }
  }, [allFareDetails.length, isMobile, restrictions, showAllAmenities]);

  const showFareNotice = fareNotice?.find((n) => n?.id === selectedFare.id);
  const handleHackerFarePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setHackerFareAnchorEl(event.currentTarget);
  };
  const handleHackerFarePopoverClose = () => {
    setHackerFareAnchorEl(null);
  };

  if (restrictions) {
    return (
      <Box className="amenities flight-card-mini">
        {restrictions.length === 0 && (
          <Typography
            variant="body1"
            className="trip-fare-empty-restriction-text"
          >
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {getEmptyRestrictionsText(fareSlice.fareShelf?.rating, t as any)}
          </Typography>
        )}

        {restrictions?.map((r, i) =>
          amenitiesCountToShow > i ? (
            <Amenity
              key={`Amenity-${i}`}
              restriction={r}
              index={index}
              isLast={
                restrictions.length > amenitiesCountToShow &&
                i === amenitiesCountToShow - 1
              }
              isMobile={isMobile}
              count={restrictions.length}
              onSeeMoreOnClick={handleSeeMoreOnClick}
            />
          ) : null
        )}
        {!!showFareNotice && !isOutgoing ? (
          <>
            <Typography
              className="fare-notice"
              variant="body2"
              onClick={(event) => handleHackerFarePopoverOpen(event)}
            >
              {showFareNotice.message}
              <IconComponent
                ariaLabel="Fare notice info icon"
                className="fare-notice-info-icon"
                name={IconName.InfoCircle}
              />
            </Typography>
            <Popover
              id="mouse-over-popover"
              className="hacker-fare-popover"
              open={!!hackerFareAnchorEl}
              anchorEl={hackerFareAnchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              onClose={handleHackerFarePopoverClose}
            >
              <Typography
                className="farenotice-info"
                variant="body2"
                onMouseLeave={handleHackerFarePopoverClose}
              >
                {showFareNotice.tooltipCopy}
              </Typography>
            </Popover>
          </>
        ) : null}
      </Box>
    );
  }
  return null;
};

export default SelectedFareRestrictions;
